"use client";

import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { LoadingButton } from "@/components/ui/loading-button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGetLoggedInUser } from "@/hooks/use-get-logged-in-user";
import { useScopedI18n } from "@/locales/client";
import { trpc } from "@/util/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle, MessageCircleQuestion } from "lucide-react";
import NextLink from "next/link";
import { useForm } from "react-hook-form";
import { z } from "zod";

const formSchema = z
    .object({
        iban: z.string().optional(),
        accountType: z.enum(["sepa", "other"]),
    })
    .superRefine((data, ctx) => {
        if (data.accountType === "sepa" && (data.iban == null || data.iban.length < 10)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "IBAN is required for SEPA transfers.",
                path: ["iban"],
            });
        }
    });

export function BankData({
    onSuccess,
    footer,
    body,
}: {
    onSuccess?: () => void;
    footer?: (children: React.ReactNode) => React.ReactNode;
    body?: (children: React.ReactNode) => React.ReactNode;
}) {
    const { data: user } = useGetLoggedInUser();
    if (user == null) return;

    return <BankDataInner user={user} onSuccess={onSuccess} footer={footer} body={body} />;
}

function BankDataInner({
    user,
    footer,
    body,
    onSuccess,
}: {
    user: {
        bankAccountIban: string | null;
        revolutAccountId: string | null;
    };
    footer?: (children: React.ReactNode) => React.ReactNode;
    body?: (children: React.ReactNode) => React.ReactNode;
    onSuccess?: () => void;
}) {
    const t = useScopedI18n("bankData");
    const utils = trpc.useUtils();

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            iban: user.bankAccountIban ?? "",
            accountType: user.revolutAccountId != null ? "other" : "sepa",
        },
    });

    const updateBankAccount = trpc.lambda.users.updateBankAccount.useMutation({
        onSuccess: async () => {
            await utils.edge.users.getLoggedIn.invalidate();
            onSuccess?.();
        },
    });

    async function onSubmit(values: z.infer<typeof formSchema>) {
        if (values.accountType === "sepa") {
            updateBankAccount.mutate({
                accountType: "sepa",
                iban: values.iban ?? "",
            });
        } else {
            updateBankAccount.mutate({
                accountType: "other",
            });
        }
    }

    const footerInternal = footer ?? ((children) => <div className="mt-4">{children}</div>);
    const bodyInternal = body ?? ((children) => <>{children}</>);

    return (
        <Form {...form}>
            <form
                onSubmit={(v) => {
                    void form.handleSubmit(onSubmit)(v);
                }}
                className="space-y-4"
            >
                {bodyInternal(
                    <div className="space-y-4">
                        <FormField
                            control={form.control}
                            name="accountType"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("accountType.label")}</FormLabel>
                                    <Select
                                        onValueChange={(value: "sepa" | "other") => {
                                            field.onChange(value);
                                        }}
                                        defaultValue={field.value}
                                    >
                                        <FormControl>
                                            <SelectTrigger className="w-full">
                                                <SelectValue placeholder={t("accountType.placeholder")} />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="sepa">{t("accountType.sepa")}</SelectItem>
                                            <SelectItem value="other">{t("accountType.other")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormDescription>{t("accountType.description")}</FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {form.watch("accountType") === "sepa" ? (
                            <>
                                <FormField
                                    control={form.control}
                                    name="iban"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{t("iban.label")}</FormLabel>
                                            <FormControl>
                                                <Input placeholder="AT 0212 0000 7034 4714" {...field} />
                                            </FormControl>
                                            <FormDescription>{t("iban.description")}</FormDescription>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </>
                        ) : (
                            <div className="space-y-4">
                                {user.revolutAccountId === "NOT YET SET" || user.revolutAccountId == null ? (
                                    <>
                                        <p className="text-muted-foreground text-sm">{t("nonSepa.message")}</p>
                                        <Button type="button" asChild size="sm" variant="outline">
                                            <NextLink href="https://wa.me/15557753729" target="_blank">
                                                <MessageCircleQuestion className="mr-1 size-4" />
                                                {t("nonSepa.contactSupport")}
                                            </NextLink>
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-center space-x-2">
                                            <CheckCircle className="size-5 text-green-500" />
                                            <p className="text-sm">{t("nonSepa.accountSet")}</p>
                                        </div>
                                        <Button type="button" asChild size="sm" variant="outline">
                                            <NextLink href="https://wa.me/15557753729" target="_blank">
                                                <MessageCircleQuestion className="mr-1 size-4" />
                                                {t("nonSepa.contactSupport")}
                                            </NextLink>
                                        </Button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>,
                )}
                {footerInternal(
                    <LoadingButton type="submit" loading={updateBankAccount.isPending}>
                        {t("buttons.save")}
                    </LoadingButton>,
                )}
            </form>
        </Form>
    );
}
